import { useCallback } from 'react';
import { TFunction } from 'i18next';
import find from 'lodash-es/find';
import { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack';

import { SECURE_API } from 'src/api/api';
import { isObjectId } from 'src/helpers/isObjectId';
import { ICheckin } from 'src/interfaces/Checkin';

type EnqueueSnackbar = (
  message: SnackbarMessage,
  options?: OptionsObject,
) => SnackbarKey;

interface PreviewCheckin {
  id: string;
  startTime?: string | Date | null;
  finishTime?: string | Date | null;
}

interface CheckinPreviewResponse {
  registration: { id: string; participantId: string; editionId: string };
  participant: { id: string; firstName: string; lastName: string };
  checkin: PreviewCheckin | null;
}

interface QrStopTimerScanResult {
  checkinId: string;
  participantName: string;
}

interface Params {
  editionId: string | null | undefined;
  checkins: ICheckin[];
  onResolved: (result: QrStopTimerScanResult) => void;
  enqueueSnackbar: EnqueueSnackbar;
  t: TFunction;
  onSuccess?: () => void;
}

function participantDisplayName(p: { firstName: string; lastName: string }): string {
  return `${p.firstName} ${p.lastName}`.trim();
}

function findCheckinInList(
  checkins: ICheckin[],
  checkinId: string,
  participantId: string,
): ICheckin | undefined {
  return find(
    checkins,
    c => c.id === checkinId || c.participant.id === participantId,
  );
}

function resolveTimerState(checkin: PreviewCheckin | ICheckin) {
  return {
    hasStart: !!checkin.startTime,
    hasFinish: !!checkin.finishTime,
  };
}

export function useQrStopTimerScan({
  editionId,
  checkins,
  onResolved,
  enqueueSnackbar,
  t,
  onSuccess,
}: Params) {
  const handleScan = useCallback(
    async (raw: string) => {
      const registrationId = raw.trim();
      if (!editionId) {
        enqueueSnackbar(t('registrations.noActiveEdition'), { variant: 'error' });
        return;
      }
      if (!isObjectId(registrationId)) {
        enqueueSnackbar(t('checkins.qrInvalid'), { variant: 'error' });
        return;
      }

      try {
        const { data } = await SECURE_API.get<CheckinPreviewResponse>(
          `/registrations/${registrationId}/checkin-preview`,
          { params: { editionId } },
        );

        if (!data.checkin) {
          enqueueSnackbar(t('timers.qrStopNotCheckedIn'), { variant: 'error' });
          return;
        }

        const checkin =
          findCheckinInList(checkins, data.checkin.id, data.participant.id) ?? data.checkin;

        const { hasStart, hasFinish } = resolveTimerState(checkin);

        if (!hasStart) {
          enqueueSnackbar(t('timers.qrStopTimerNotStarted'), { variant: 'error' });
          return;
        }

        if (hasFinish) {
          enqueueSnackbar(t('timers.qrStopTimerAlreadyStopped'), { variant: 'error' });
          return;
        }

        onSuccess?.();
        onResolved({
          checkinId: checkin.id,
          participantName: participantDisplayName(data.participant),
        });
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { errorCode?: string } } };
        const errorCode = axiosErr.response?.data?.errorCode;
        if (errorCode === 'qrWrongEdition') {
          enqueueSnackbar(t('checkins.qrWrongEdition'), { variant: 'error' });
        } else {
          enqueueSnackbar(t('checkins.qrNotFound'), { variant: 'error' });
        }
      }
    },
    [checkins, editionId, enqueueSnackbar, onResolved, onSuccess, t],
  );

  return { handleScan };
}
